import { safeFetchAPI } from '@/api';
import { log, utils } from '@/helpers';
import { CampaignsV2Response, CoMarketingEditComment } from '@/types';
import { cloneDeep } from 'lodash';
import { create } from 'zustand';
import { useCoMarketingStore } from './useCoMarketingStore';

export type CoMarketingEditingCommentsStoreState = {
	loaded: boolean;
	loading: boolean;
	sortedComments: CoMarketingEditComment[] | undefined;
	comments: Record<number, Partial<CoMarketingEditComment>> | undefined;
};

interface CoMarketingEditingCommentsStoreActions {
	init: (templateID: number, requestID: number) => Promise<void>;

	createComment: (comment: CoMarketingEditComment, requestID: number) => Promise<void>;
	updateComment: (comment: CoMarketingEditComment, requestID: number) => Promise<void>;
	deleteComment: (comment: CoMarketingEditComment, requestID: number) => Promise<void>;

	reset: (loaded?: boolean) => void;
}

export type CoMarketingEditingCommentsStore = CoMarketingEditingCommentsStoreState & CoMarketingEditingCommentsStoreActions;

const initialState: CoMarketingEditingCommentsStoreState = {
	loaded: false,
	loading: false,
	comments: {},
	sortedComments: [],
};

export const useCoMarketingEditingCommentsStore = create<CoMarketingEditingCommentsStore>((set, get) => ({
	...initialState,

	async init(templateID: number, requestID: number) {
		log('init useCoMarketingEditingCommentsStore', templateID, requestID);
		if (!templateID || !requestID) {
			return;
		}

		set({ ...initialState, loading: true });

		const [comments, error] = await safeFetchAPI<CoMarketingEditComment[]>(`/co-marketing/installed/edit/comment/:uid/${templateID}/${requestID}`);

		if (error) {
			utils?.toaster?.addToast?.({
				message: error ?? 'Error fetching comments',
				type: 'danger',
			});
		}

		const mappedComments = comments?.reduce(
			(acc, comment) => {
				acc[comment.id] = comment;
				return acc;
			},
			{} as Record<number, CoMarketingEditComment>,
		);

		set({ comments: mappedComments, loaded: true, loading: false, sortedComments: comments });
	},

	async createComment(comment: CoMarketingEditComment, requestID: number) {
		set({ loading: true });

		const templateID = useCoMarketingStore.getState().installedTemplate?.id;

		if (!templateID) {
			utils?.toaster?.addToast?.({
				message: 'Template ID not found',
				type: 'danger',
			});
			set({ loading: false });
			return;
		}

		const [createdComment, error] = await safeFetchAPI<CoMarketingEditComment>(
			`/co-marketing/installed/edit/comment/:uid/${templateID}/${requestID}`, //

			{
				method: 'POST',
				payload: comment,
			},
		);

		if (error) {
			utils?.toaster?.addToast?.({
				message: error ?? 'Error creating comment',
				type: 'danger',
			});
			set({ loading: false });
			return;
		}

		set({
			comments: { ...get().comments, [createdComment?.id ?? 0]: createdComment ?? {} },
			loading: false,
		});
	},

	async updateComment(comment: CoMarketingEditComment, requestID: number) {
		set({ loading: true });

		const templateID = useCoMarketingStore.getState().installedTemplate?.id;

		if (!templateID) {
			utils?.toaster?.addToast?.({
				message: 'Template ID not found',
				type: 'danger',
			});
			set({ loading: false });
			return;
		}

		const [updatedComment, error] = await safeFetchAPI<CoMarketingEditComment>(
			`/co-marketing/installed/edit/comment/:uid/${templateID}/${requestID}/${comment.id}`,
			{
				method: 'PUT',
				payload: comment,
			},
		);

		if (error) {
			utils?.toaster?.addToast?.({
				message: error ?? 'Error updating comment',
				type: 'danger',
			});
			set({ loading: false });
			return;
		}

		set({
			comments: { ...get().comments, [updatedComment?.id ?? 0]: updatedComment ?? {} },
			loading: false,
		});
	},

	async deleteComment(comment: CoMarketingEditComment, requestID: number) {
		set({ loading: true });

		const templateID = useCoMarketingStore.getState().installedTemplate?.id;

		if (!templateID) {
			utils?.toaster?.addToast?.({
				message: 'Template ID not found',
				type: 'danger',
			});
			set({ loading: false });
			return;
		}

		const [deletedComment, error] = await safeFetchAPI<CoMarketingEditComment>(
			`/co-marketing/installed/edit/comment/:uid/${templateID}/${requestID}/${comment.id}`,
			{
				method: 'DELETE',
				payload: comment,
			},
		);

		if (error) {
			utils?.toaster?.addToast?.({
				message: error ?? 'Error deleting comment',
				type: 'danger',
			});
			set({ loading: false });
			return;
		}

		const comments = cloneDeep(get().comments ?? {});
		delete comments[deletedComment?.id ?? 0];

		set({
			comments,
			loading: false,
		});
	},

	reset: (loaded?: boolean) => set({ ...initialState, loaded }),
}));
